import { getState } from '../api/rpc';
import type { GameState } from '../api/types';
import { RpcError, toRpcError } from '../api/errors';
import { syncServerTime } from './clock';
import { Emitter } from './emitter';
import { config } from '../config';

export interface StateChange {
  state: GameState;
  /** 前回の状態。最初の取得では null */
  prev: GameState | null;
}

/**
 * get_state を唯一の正として持つ。Realtime は使わない（合宿版で直接 SELECT を禁止したため）。
 * 操作のあとは refresh() で即座に取り直し、ほかは20秒ごとに取り直す（画面が見えているときだけ）。
 */
class Store {
  readonly changed = new Emitter<StateChange>();
  readonly failed = new Emitter<RpcError>();
  private cur: GameState | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private inflight: Promise<GameState | null> | null = null;

  get state(): GameState | null {
    return this.cur;
  }

  async refresh(): Promise<GameState | null> {
    // 同時に何度呼ばれても通信は1本にまとめる
    this.inflight ??= this.load().finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  private async load(): Promise<GameState | null> {
    const sent = Date.now();
    try {
      const next = await getState();
      syncServerTime(next.server_time, sent, Date.now());
      const prev = this.cur;
      this.cur = next;
      this.changed.emit({ state: next, prev });
      return next;
    } catch (e) {
      this.failed.emit(toRpcError(e));
      return null;
    }
  }

  start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => {
      if (document.visibilityState === 'visible') void this.refresh();
    }, config.pollMs);
    document.addEventListener('visibilitychange', this.onVisible);
    void this.refresh();
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  private onVisible = (): void => {
    if (document.visibilityState === 'visible') void this.refresh();
  };

  /** テスト用 */
  _set(s: GameState | null): void {
    const prev = this.cur;
    this.cur = s;
    if (s) this.changed.emit({ state: s, prev });
  }
}

export const store = new Store();
